import { dayjsLocal, dayjs } from '@frappe-ui/utils/dayjs.ts'
import { getConfig } from '@frappe-ui/utils/config.ts'

// Formats commonly used in Vietnam. Keep API/storage formats separate from
// these display formats so changing the UI never changes persisted data.
export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY'
export const DEFAULT_TIME_FORMAT = 'HH:mm'
export const DEFAULT_DATETIME_FORMAT = `${DEFAULT_DATE_FORMAT} ${DEFAULT_TIME_FORMAT}`
export const ISO_DATE_FORMAT = 'YYYY-MM-DD'
export const ISO_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

function formatValue(value, format) {
  if (!value) return ''

  const date = dayjsLocal(value)
  return date.isValid() ? date.format(format) : ''
}

/** Format a date for display, defaulting to dd/MM/yyyy. */
export function formatDate(value, format = DEFAULT_DATE_FORMAT) {
  return formatValue(value, format)
}

/** Format a time for display, defaulting to the 24-hour Vietnamese convention. */
export function formatTime(value, format = DEFAULT_TIME_FORMAT) {
  return formatValue(value, format)
}

/** Format a date and time for display, defaulting to dd/MM/yyyy HH:mm. */
export function formatDateTime(value, format = DEFAULT_DATETIME_FORMAT) {
  return formatValue(value, format)
}

/** Format a value for API/storage use. */
export function formatISODate(value) {
  return formatValue(value, ISO_DATE_FORMAT)
}

export function formatISODateTime(value) {
  return formatValue(value, ISO_DATETIME_FORMAT)
}

export function formatDuration(totalSeconds, longForm = false) {
  if (totalSeconds === null || totalSeconds === undefined || totalSeconds === '') return ''
  const seconds = parseInt(totalSeconds, 10)
  if (isNaN(seconds)) return ''
  if (seconds === 0) return longForm ? '0 seconds' : '0s'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60
  if (longForm) {
    const parts = []
    if (hours) parts.push(hours === 1 ? '1 hour' : `${hours} hours`)
    if (minutes) parts.push(minutes === 1 ? '1 minute' : `${minutes} minutes`)
    if (remainingSeconds) parts.push(remainingSeconds === 1 ? '1 second' : `${remainingSeconds} seconds`)
    return parts.join(' ')
  }
  return [hours && `${hours}h`, minutes && `${minutes}m`, remainingSeconds && `${remainingSeconds}s`]
    .filter(Boolean)
    .join(' ')
}

export function getFormat(date, format, onlyDate = false, onlyTime = false, withDate = true) {
  if (!date && withDate) return ''
  const sysdefaults = typeof window !== 'undefined' ? window.sysdefaults || {} : {}
  const dateFormat = sysdefaults.date_format
    ?.replace('mm', 'MM')
    .replace('yyyy', 'YYYY')
    .replace('dd', 'DD') || DEFAULT_DATE_FORMAT
  const timeFormat = sysdefaults.time_format || DEFAULT_TIME_FORMAT
  let resolvedFormat = format || DEFAULT_DATETIME_FORMAT
  if (onlyDate) resolvedFormat = dateFormat
  if (onlyTime) resolvedFormat = timeFormat
  if (onlyTime && onlyDate) resolvedFormat = `${dateFormat} ${timeFormat}`
  return withDate ? dayjs(date).format(resolvedFormat) : resolvedFormat
}

export function timeAgo(date) {
  return prettyDate(date)
}

function getBrowserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export function prettyDate(value, mini = false) {
  if (!value) return ''
  const systemTimezone = getConfig('systemTimezone')
  const localTimezone = getConfig('localTimezone') || getBrowserTimezone()
  const date = typeof value === 'string' ? dayjsLocal(value) : value
  const now = dayjs().tz(localTimezone || systemTimezone)
  const diff = now.diff(date, 'seconds')
  const dayDiff = diff / 86400
  if (isNaN(dayDiff)) return ''

  const future = dayDiff < 0
  const absoluteDays = Math.abs(dayDiff)
  const minutes = Math.floor(Math.abs(diff) / 60)
  const hours = Math.floor(Math.abs(diff) / 3600)
  const days = Math.floor(absoluteDays)

  if (mini) {
    if (absoluteDays < 1) {
      if (Math.abs(diff) < 60) return __('now')
      if (Math.abs(diff) < 3600) return __(future ? 'in {0} m' : '{0} m', [minutes])
      return __(future ? 'in {0} h' : '{0} h', [hours])
    }
    if (absoluteDays < 1.5) return __(future ? 'tomorrow' : '{0} d', [days])
    if (absoluteDays < 7) return __(future ? 'in {0} d' : '{0} d', [days])
    if (absoluteDays < 31) return __(future ? 'in {0} w' : '{0} w', [Math.floor(days / 7)])
    if (absoluteDays < 365) return __(future ? 'in {0} M' : '{0} M', [Math.floor(days / 30)])
    return __(future ? 'in {0} y' : '{0} y', [Math.floor(days / 365)])
  }

  if (absoluteDays < 1) {
    if (Math.abs(diff) < 60) return __('just now')
    if (Math.abs(diff) < 120) return __(future ? 'in 1 minute' : '1 minute ago')
    if (Math.abs(diff) < 3600) return __(future ? 'in {0} minutes' : '{0} minutes ago', [minutes])
    if (Math.abs(diff) < 7200) return __(future ? 'in 1 hour' : '1 hour ago')
    return __(future ? 'in {0} hours' : '{0} hours ago', [hours])
  }
  if (absoluteDays < 1.5) return __(future ? 'tomorrow' : 'yesterday')
  if (absoluteDays < 7) return __(future ? 'in {0} days' : '{0} days ago', [days])
  if (absoluteDays < 31) return __(future ? 'in {0} weeks' : '{0} weeks ago', [Math.floor(days / 7)])
  if (absoluteDays < 365) return __(future ? 'in {0} months' : '{0} months ago', [Math.floor(days / 30)])
  return __(future ? 'in {0} years' : '{0} years ago', [Math.floor(days / 365)])
}
